import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import LogoPrimary from '@/assets/images/logo-principal-dark.svg';
import LogoLight from '@/assets/images/logo-icone-light.svg';
import LogoDark from '@/assets/images/logo-icone-dark.svg';
import { LanguageSelect } from '@/components/ui/language-select';
import { useUIStore } from '@/stores/ui';
import LabelInput from '@/components/Input/LabelInput';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/custom-select';
import { useQuery, useMutation } from '@tanstack/react-query';
import { fetchToken, updatePassword } from '../services/authService';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_NUMBER_REGEX,
  PASSWORD_SPECIAL_REGEX,
  PASSWORD_UPPERCASE_REGEX,
} from '@/utils/passwordRules';

export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentLanguage, setLanguage, currentTheme } = useUIStore();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const languages = [
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ];

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>();

  const {
    data: tokenData,
    isLoading: isValidatingToken,
    isError: isTokenError,
    isSuccess: isTokenSuccess,
  } = useQuery({
    queryKey: ['validateToken', token],
    queryFn: () => fetchToken(token),
    enabled: !!token,
    retry: false,
  });

  const updatePasswordMutation = useMutation({
    mutationFn: ({
      token,
      password,
      userId,
    }: {
      token: string;
      password: string;
      userId?: number;
    }) => updatePassword(token, password, userId),
    onSuccess: () => {
      toast({
        title:
          t('auth.password_reset_success') || 'Senha redefinida com sucesso!',
        variant: 'default',
      });
      navigate('/session/login');
    },
    onError: (error: any) => {
      toast({
        title: t('auth.password_reset_error') || 'Erro ao redefinir senha',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (isTokenError) {
      navigate('/session/login');
    }
  }, [isTokenError]);

  const accounts = tokenData?.data?.accounts || tokenData?.accounts || [];

  useEffect(() => {
    if (accounts.length === 1) {
      setSelectedUserId(accounts[0].user_id);
    }
  }, [tokenData]);

  if (!token) {
    return <Navigate to="/session/login" replace />;
  }

  if (isValidatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <div className="bg-background p-8 rounded-lg shadow-lg">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-foreground">
              {t('auth.validating_token') || 'Validando token...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isTokenSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <div className="bg-background p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="flex flex-col items-center">
            <img src={LogoPrimary} alt="Logo" className="w-36 h-12 mb-12" />
            <h2 className="text-2xl font-bold text-secondary mb-4">
              {t('auth.token_already_used') || 'Token Já Utilizado'}
            </h2>
            <p className="text-muted-foreground mb-12">
              {t('auth.token_already_used_description') ||
                'Este token já foi utilizado. Solicite um novo link de redefinição de senha.'}
            </p>
            <button
              onClick={() => navigate('/session/login')}
              className="btn-primary w-full py-3 px-4 rounded-lg font-medium text-white"
            >
              {t('auth.back_to_login') || 'Voltar ao Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (accounts.length > 1 && !selectedUserId) {
      newErrors.user_id =
        t('auth.select_account_required') || 'Selecione o usuário.';
    }

    if (!formData.password) {
      newErrors.password = t('auth.required_field') || 'Campo obrigatório';
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      newErrors.password =
        t('common.password_min_length') ||
        'A senha deve ter no mínimo 8 caracteres';
    } else if (!PASSWORD_UPPERCASE_REGEX.test(formData.password)) {
      newErrors.password =
        t('common.password_uppercase') ||
        'A senha deve conter ao menos uma letra maiúscula';
    } else if (!PASSWORD_NUMBER_REGEX.test(formData.password)) {
      newErrors.password =
        t('common.password_number') ||
        'A senha deve conter ao menos um número';
    } else if (!PASSWORD_SPECIAL_REGEX.test(formData.password)) {
      newErrors.password =
        t('common.password_special') ||
        'A senha deve conter ao menos um caractere especial';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword =
        t('auth.required_field') || 'Campo obrigatório';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword =
        t('auth.passwords_dont_match') || 'As senhas não coincidem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    updatePasswordMutation.mutate({
      token,
      password: formData.password,
      userId: selectedUserId,
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex flex-row-reverse bg-primary">
      <div className="flex-[1.3] flex items-center justify-center">
        <div className="w-full h-full bg-background p-4 rounded-l-3xl flex items-center justify-center px-8 shadow-lg dark:shadow-none">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-lg"
          >
            <div className="text-center mb-8">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl font-bold text-foreground mb-4"
              >
                {t('auth.reset_password') || 'Redefinir Senha'}
              </motion.h1>
            </div>

            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {accounts.length > 1 && (
                <div className="space-y-2" data-testid="reset-account-select">
                  <LabelInput
                    htmlFor="reset-user"
                    title={
                      t('auth.select_account') ||
                      'Qual usuário deve ter a senha redefinida?'
                    }
                  />
                  <Select
                    id="reset-user"
                    options={accounts.map((account: any) => ({
                      value: account.user_id,
                      label: account.username,
                    }))}
                    value={
                      accounts
                        .map((account: any) => ({
                          value: account.user_id,
                          label: account.username,
                        }))
                        .find((option: any) => option.value === selectedUserId) ||
                      null
                    }
                    onChange={(option: any) => {
                      setSelectedUserId(option?.value);
                      setErrors((current) => ({ ...current, user_id: '' }));
                    }}
                    placeholder={t('auth.select_account_placeholder') || 'Selecione o usuário'}
                  />
                  {errors.user_id && (
                    <p className="text-sm text-destructive">{errors.user_id}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {t('auth.select_account_help') ||
                      'A nova senha será aplicada somente ao usuário selecionado.'}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <LabelInput
                  htmlFor="password"
                  title={t('auth.new_password') || 'Nova Senha'}
                />
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange('password', e.target.value)
                    }
                    className={`h-16 bg-transparent
                    ${errors.password ? 'border-destructive dark:border-red-500' : 'border-border '}
                  `}
                    placeholder={
                      t('auth.input_new_password') || 'Digite sua nova senha'
                    }
                    disabled={updatePasswordMutation.isPending}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={updatePasswordMutation.isPending}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-destructive dark:text-red-400"
                  >
                    {errors.password}
                  </motion.p>
                )}
              </div>

              <div className="space-y-2">
                <LabelInput
                  htmlFor="confirmPassword"
                  title={t('auth.confirm_password') || 'Confirmar Senha'}
                />
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange('confirmPassword', e.target.value)
                    }
                    className={`h-16 bg-transparent
                    ${errors.confirmPassword ? 'border-destructive dark:border-red-500' : 'border-border '}
                  `}
                    placeholder={
                      t('auth.input_confirm_password') ||
                      'Confirme sua nova senha'
                    }
                    disabled={updatePasswordMutation.isPending}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={updatePasswordMutation.isPending}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-destructive dark:text-red-400"
                  >
                    {errors.confirmPassword}
                  </motion.p>
                )}
              </div>

              <motion.button
                type="submit"
                disabled={updatePasswordMutation.isPending}
                className="btn-primary w-full py-3 px-4 rounded-lg font-medium text-white dark:bg-blue-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 mt-6"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {updatePasswordMutation.isPending ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Lock className="h-5 w-5" />
                    <span>
                      {t('auth.reset_password_button') || 'Redefinir Senha'}
                    </span>
                  </>
                )}
              </motion.button>
            </motion.form>
          </motion.div>
        </div>
      </div>

      <div className="hidden lg:flex flex-[0.7] relative z-10">
        <div className="absolute top-8 left-8">
          <motion.img
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            src={currentTheme === 'dark' ? LogoDark : LogoLight}
            alt="Logo"
            className="w-20 h-20"
          />
          <div className="fixed bottom-6 right-6 z-50">
            <LanguageSelect
              languages={languages}
              currentLanguage={currentLanguage}
              onLanguageChange={setLanguage}
              collapsed={true}
              position="top"
              align="right"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
